// ==============================|| AURORA NIGHT - MEMBER AREA TOKENS ||============================== //
// ธีมหน้าหลังล็อกอิน: พื้นกรมท่าเข้ม + แสงออโรร่าม่วง/ฟ้า, สีฝาก = มิ้นท์, สีถอน = คอรัล

export const FONT_BODY = `'IBM Plex Sans Thai', 'Prompt', sans-serif`;
export const FONT_DISPLAY = `'Prompt', 'IBM Plex Sans Thai', sans-serif`;

// ใช้กับ ThemeProvider แบบ function: เปลี่ยนฟอนต์ทุก variant เป็นฟอนต์ไทยของธีม
export const withMemberFonts = (outer) => {
  const typography = { ...outer.typography, fontFamily: FONT_BODY };
  Object.keys(typography).forEach((key) => {
    const variant = typography[key];
    if (variant && typeof variant === "object" && "fontFamily" in variant) {
      typography[key] = {
        ...variant,
        fontFamily: /^h[1-6]$/.test(key) ? FONT_DISPLAY : FONT_BODY,
      };
    }
  });
  return { ...outer, typography };
};

export const C = {
  bg: "#070A14",
  bgDeep: "#05070F",
  surface: "rgba(22, 28, 52, 0.62)",
  surfaceSolid: "#111733",
  surfaceRaised: "rgba(255, 255, 255, 0.05)",
  border: "rgba(160, 170, 255, 0.12)",
  borderStrong: "rgba(160, 170, 255, 0.24)",

  text: "#EEF1FF",
  textDim: "#A3ABCF",
  textMute: "#6B7399",

  violet: "#7C5CFF",
  blue: "#4F7BFF",
  cyan: "#22D3EE",
  pink: "#EC4899",
  mint: "#2DE3A0",
  mintDeep: "#0FB57E",
  coral: "#FF6B8B",
  coralDeep: "#E9365E",
  gold: "#FFC857",
  goldDeep: "#F59E0B",
};

export const G = {
  primary: `linear-gradient(135deg, ${C.violet} 0%, ${C.blue} 55%, ${C.cyan} 100%)`,
  play: `linear-gradient(135deg, #8B5CF6 0%, #C026D3 55%, ${C.pink} 100%)`,
  deposit: `linear-gradient(135deg, #5CF2BE 0%, ${C.mint} 45%, ${C.mintDeep} 100%)`,
  withdraw: `linear-gradient(135deg, #FF9AAE 0%, ${C.coral} 45%, ${C.coralDeep} 100%)`,
  gold: `linear-gradient(135deg, #FFE7A3 0%, ${C.gold} 45%, ${C.goldDeep} 100%)`,
  support: `linear-gradient(135deg, ${C.cyan} 0%, ${C.blue} 100%)`,
  hero: "linear-gradient(140deg, #2A1D6B 0%, #1A2A7A 45%, #0E3D63 100%)",
};

// การ์ดกระจกพื้นฐาน ใช้ทุก section
export const glassCard = (extra = {}) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: "24px",
  background: C.surface,
  backdropFilter: "blur(18px) saturate(140%)",
  WebkitBackdropFilter: "blur(18px) saturate(140%)",
  border: `1px solid ${C.border}`,
  boxShadow: "0 20px 50px rgba(2, 4, 16, 0.45), inset 0 1px 0 rgba(255,255,255,0.05)",
  color: C.text,
  ...extra,
});

// paper ของ Dialog ในโซนสมาชิก
export const dialogSx = (accent = C.violet, extra = {}) => ({
  "& .MuiDialog-paper": {
    position: "relative",
    overflow: "hidden",
    borderRadius: "28px",
    background: `radial-gradient(120% 70% at 50% -10%, ${accent}33 0%, transparent 60%), ${C.surfaceSolid}`,
    border: `1px solid ${C.borderStrong}`,
    boxShadow: `0 40px 100px rgba(0,0,0,0.7), 0 0 0 1px ${accent}22`,
    color: C.text,
    fontFamily: FONT_BODY,
    ...extra,
  },
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(10px)",
    background: "rgba(3, 5, 14, 0.72)",
  },
});

export const sectionTitleSx = {
  fontFamily: FONT_DISPLAY,
  fontWeight: 700,
  fontSize: { xs: 17, sm: 19 },
  color: C.text,
  letterSpacing: "-0.2px",
};

export const eyebrowSx = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: C.textMute,
};

// ช่องกรอกข้อมูลโทนมืด
export const inputSx = (accent = C.violet) => ({
  "& .MuiInputLabel-root": { color: C.textDim, fontFamily: FONT_BODY },
  "& .MuiInputLabel-root.Mui-focused": { color: accent },
  "& .MuiOutlinedInput-root": {
    color: C.text,
    borderRadius: "16px",
    background: "rgba(255,255,255,0.04)",
    fontFamily: FONT_BODY,
    "& input": { background: "transparent", color: C.text },
    "& fieldset": { borderColor: C.borderStrong },
    "&:hover fieldset": { borderColor: `${accent}99` },
    "&.Mui-focused fieldset": { borderColor: accent, borderWidth: "1.5px" },
    "&.Mui-disabled": { opacity: 0.55 },
    "&.Mui-disabled input": { WebkitTextFillColor: C.textDim },
  },
});

export const pillButtonSx = (gradient, color = "#08111F", glow = "rgba(124,92,255,0.35)") => ({
  borderRadius: "16px",
  fontFamily: FONT_DISPLAY,
  fontWeight: 600,
  textTransform: "none",
  color,
  background: gradient,
  boxShadow: `0 10px 28px ${glow}`,
  transition: "transform .2s ease, box-shadow .2s ease, filter .2s ease",
  "&:hover": {
    background: gradient,
    filter: "brightness(1.08)",
    transform: "translateY(-1px)",
    boxShadow: `0 14px 34px ${glow}`,
  },
  "&.Mui-disabled": {
    background: "rgba(255,255,255,0.06)",
    color: C.textMute,
    boxShadow: "none",
  },
});

export const ghostButtonSx = {
  borderRadius: "14px",
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: "none",
  color: C.textDim,
  border: `1px solid ${C.borderStrong}`,
  background: "rgba(255,255,255,0.03)",
  "&:hover": { background: "rgba(255,255,255,0.08)", borderColor: C.borderStrong, color: C.text },
};

export const formatMoney = (value) => {
  if (value === null || value === undefined || value === "" || Array.isArray(value)) return "0.00";
  return Number(value).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ช่องกรอกขนาดใหญ่สำหรับหน้า login / สมัคร
export const authFieldSx = (accent = C.violet) => ({
  color: C.text,
  height: 56,
  borderRadius: "16px",
  background: "rgba(255,255,255,0.04)",
  fontFamily: FONT_BODY,
  fontSize: 15.5,
  transition: "background .2s ease, box-shadow .2s ease",
  "& .MuiOutlinedInput-input": {
    color: C.text,
    background: "transparent",
    fontWeight: 500,
    "&::placeholder": { color: C.textMute, opacity: 1 },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: C.borderStrong, borderRadius: "16px" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: `${accent}99` },
  "&.Mui-focused": { background: "rgba(124,92,255,0.06)", boxShadow: `0 0 0 4px ${accent}22` },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: accent, borderWidth: "1.5px" },
  "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: C.coral },
  "& .MuiOutlinedInput-input:-webkit-autofill": {
    WebkitBoxShadow: "0 0 0 100px #151B38 inset",
    WebkitTextFillColor: C.text,
    caretColor: C.text,
    borderRadius: 0,
  },
  "& .MuiInputAdornment-root .MuiSvgIcon-root": { color: C.textMute },
  "& .MuiSelect-icon": { color: C.textDim },
});

export const authLabelSx = {
  display: "block",
  mb: 0.75,
  fontSize: 13,
  fontWeight: 600,
  color: C.textDim,
};
